import { useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import type { MotionCue, StageStateV1, Vector3 } from '@shotstead/contracts'

export const THREE_RUNTIME_ADAPTER_ID = 'shotstead-three-r3f'
export const THREE_RUNTIME_ADAPTER_VERSION = 'as0.1'

const clamp01 = (value: number) => Math.min(1, Math.max(0, value))

const toTuple = (value: Vector3): [number, number, number] => [value.x, value.y, value.z]

const lerpVector = (from: Vector3, to: Vector3, t: number): Vector3 => ({
  x: from.x + (to.x - from.x) * t,
  y: from.y + (to.y - from.y) * t,
  z: from.z + (to.z - from.z) * t,
})

function findMotionCue(stage: StageStateV1, performerId: string): MotionCue | undefined {
  return stage.motion_cues.find(
    (cue) => cue.performer_id === performerId && cue.action === 'walk' && cue.path.length > 0,
  )
}

export function performerPositionAtTime(
  stage: StageStateV1,
  performerId: string,
  elapsedSeconds: number,
): Vector3 {
  const performer = stage.performers.find((item) => item.performer_id === performerId)
  if (!performer) return { x: 0, y: 0, z: 0 }

  const cue = findMotionCue(stage, performerId)
  if (!cue || cue.path.length === 0) return performer.transform.position

  const localTime = elapsedSeconds - cue.start_seconds
  if (localTime <= cue.path[0].t_seconds) return cue.path[0].position

  for (let index = 1; index < cue.path.length; index += 1) {
    const previous = cue.path[index - 1]
    const next = cue.path[index]
    if (localTime <= next.t_seconds) {
      const span = Math.max(0.0001, next.t_seconds - previous.t_seconds)
      const progress = clamp01((localTime - previous.t_seconds) / span)
      return lerpVector(previous.position, next.position, progress)
    }
  }

  return cue.path[cue.path.length - 1].position
}

export function resolveStageRefPosition(
  stage: StageStateV1,
  ref: string | null,
  elapsedSeconds: number,
): Vector3 | null {
  if (!ref) return null

  const performer = stage.performers.find((item) => item.performer_id === ref)
  if (performer) return performerPositionAtTime(stage, performer.performer_id, elapsedSeconds)

  const prop = stage.props.find((item) => item.prop_id === ref)
  if (prop) return prop.transform.position

  return null
}

export function cameraPositionAtTime(stage: StageStateV1, elapsedSeconds: number): Vector3 {
  const base = stage.camera.transform.position
  const movement = stage.camera.movement
  if (!movement || movement.kind === 'fixed' || movement.duration_seconds <= 0) return base

  const progress = clamp01(
    (elapsedSeconds - movement.start_seconds) / Math.max(0.0001, movement.duration_seconds),
  )

  if (movement.kind === 'lateral') {
    return {
      x: base.x + movement.distance_meters * progress,
      y: base.y,
      z: base.z,
    }
  }

  const target = resolveStageRefPosition(stage, stage.camera.target_ref, elapsedSeconds)
  if (!target) return base

  const direction = new THREE.Vector3(target.x - base.x, target.y - base.y, target.z - base.z)
  if (direction.lengthSq() === 0) return base
  direction.normalize().multiplyScalar(movement.distance_meters * progress)

  return {
    x: base.x + direction.x,
    y: base.y + direction.y,
    z: base.z + direction.z,
  }
}

export function rehearsalDurationSeconds(stage: StageStateV1): number {
  const cueEnds = stage.motion_cues.map((cue) => cue.start_seconds + cue.duration_seconds)
  const cameraEnd = stage.camera.movement
    ? stage.camera.movement.start_seconds + stage.camera.movement.duration_seconds
    : 0
  return Math.max(1, cameraEnd, ...cueEnds)
}

function DirectorCameraRig() {
  const { camera } = useThree()

  useEffect(() => {
    camera.position.set(7, 5, 7)
    camera.lookAt(0, 1, 0)
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.fov = 48
      camera.updateProjectionMatrix()
    }
  }, [camera])

  return null
}

export function CaptureCameraRig({
  stage,
  elapsedSeconds,
}: {
  stage: StageStateV1
  elapsedSeconds: number
}) {
  const { camera } = useThree()

  useFrame(() => {
    const position = cameraPositionAtTime(stage, elapsedSeconds)
    camera.position.set(position.x, position.y, position.z)

    const target = resolveStageRefPosition(stage, stage.camera.target_ref, elapsedSeconds)
    if (target) {
      camera.lookAt(target.x, target.y + 1.0, target.z)
    } else {
      const rotation = stage.camera.transform.rotation
      camera.rotation.set(rotation.x, rotation.y, rotation.z)
    }

    if (camera instanceof THREE.PerspectiveCamera && camera.fov !== stage.camera.fov_degrees) {
      camera.fov = stage.camera.fov_degrees
      camera.updateProjectionMatrix()
    }
  })

  return null
}

function ProceduralRoom() {
  return (
    <group>
      <mesh position={[0, -0.05, 0]} receiveShadow>
        <boxGeometry args={[6, 0.1, 5]} />
        <meshStandardMaterial color="#b7ae9d" roughness={0.85} />
      </mesh>
      <mesh position={[0, 1.5, -2.5]} receiveShadow>
        <boxGeometry args={[6, 3, 0.1]} />
        <meshStandardMaterial color="#d6d1c7" roughness={0.9} />
      </mesh>
      <mesh position={[-3, 1.5, 0]} receiveShadow>
        <boxGeometry args={[0.1, 3, 5]} />
        <meshStandardMaterial color="#cbc5ba" roughness={0.9} />
      </mesh>
      <mesh position={[3, 1.5, 0]} receiveShadow>
        <boxGeometry args={[0.1, 3, 5]} />
        <meshStandardMaterial color="#cbc5ba" roughness={0.9} />
      </mesh>

      <mesh position={[-2.93, 1.05, 1.45]}>
        <boxGeometry args={[0.04, 2.1, 0.95]} />
        <meshStandardMaterial color="#5a4637" />
      </mesh>
      <mesh position={[1.45, 1.8, -2.43]}>
        <boxGeometry args={[1.6, 1.1, 0.04]} />
        <meshStandardMaterial color="#8db6c7" emissive="#162c35" emissiveIntensity={0.35} />
      </mesh>

      <group position={[0.65, 0, 0.2]}>
        <mesh position={[0, 0.74, 0]} castShadow receiveShadow>
          <boxGeometry args={[1.5, 0.12, 0.8]} />
          <meshStandardMaterial color="#72543d" roughness={0.75} />
        </mesh>
        {[
          [-0.62, 0.36, -0.28],
          [0.62, 0.36, -0.28],
          [-0.62, 0.36, 0.28],
          [0.62, 0.36, 0.28],
        ].map((position, index) => (
          <mesh key={index} position={position as [number, number, number]} castShadow>
            <boxGeometry args={[0.08, 0.72, 0.08]} />
            <meshStandardMaterial color="#644833" />
          </mesh>
        ))}
      </group>
    </group>
  )
}

function Performer({ stage, elapsedSeconds }: { stage: StageStateV1; elapsedSeconds: number }) {
  const performer = stage.performers[0]
  if (!performer) return null

  const position = performerPositionAtTime(stage, performer.performer_id, elapsedSeconds)
  const rotation = performer.transform.rotation

  return (
    <group position={toTuple(position)} rotation={toTuple(rotation)}>
      <mesh position={[0, 0.9, 0]} castShadow>
        <boxGeometry args={[0.48, 1.25, 0.3]} />
        <meshStandardMaterial color="#b45e52" roughness={0.8} />
      </mesh>
      <mesh position={[0, 1.72, 0]} castShadow>
        <sphereGeometry args={[0.24, 20, 12]} />
        <meshStandardMaterial color="#d9b39a" roughness={0.9} />
      </mesh>
    </group>
  )
}

function Props({ stage }: { stage: StageStateV1 }) {
  return (
    <>
      {stage.props.map((prop) => (
        <mesh
          key={prop.prop_id}
          position={toTuple(prop.transform.position)}
          rotation={toTuple(prop.transform.rotation)}
          scale={toTuple(prop.transform.scale)}
          castShadow
        >
          <boxGeometry args={[0.28, 0.02, 0.2]} />
          <meshStandardMaterial color="#eee6d4" roughness={0.95} />
        </mesh>
      ))}
    </>
  )
}

function CaptureCameraMarker({ stage, elapsedSeconds }: { stage: StageStateV1; elapsedSeconds: number }) {
  const position = cameraPositionAtTime(stage, elapsedSeconds)
  return (
    <mesh position={toTuple(position)}>
      <boxGeometry args={[0.25, 0.16, 0.35]} />
      <meshStandardMaterial color="#22262d" wireframe />
    </mesh>
  )
}

export function StageScene({
  stage,
  elapsedSeconds,
  directorView = false,
}: {
  stage: StageStateV1
  elapsedSeconds: number
  directorView?: boolean
}) {
  const environment = stage.environment.transform

  return (
    <>
      <color attach="background" args={[directorView ? '#12171d' : '#0b0e12']} />
      <ambientLight intensity={1.2} />
      <directionalLight position={[4, 6, 3]} intensity={2.1} castShadow />
      <pointLight position={[-1.5, 2.3, 1.5]} intensity={18} distance={7} color="#ffd6a6" />

      {directorView ? <DirectorCameraRig /> : <CaptureCameraRig stage={stage} elapsedSeconds={elapsedSeconds} />}

      <group
        position={toTuple(environment.position)}
        rotation={toTuple(environment.rotation)}
        scale={toTuple(environment.scale)}
      >
        <ProceduralRoom />
        <Performer stage={stage} elapsedSeconds={elapsedSeconds} />
        <Props stage={stage} />
        {directorView ? <CaptureCameraMarker stage={stage} elapsedSeconds={elapsedSeconds} /> : null}
      </group>
    </>
  )
}
